import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import axios from 'axios';
import { WalletWidget } from '@/components/risk/WalletWidget';
import { WalletManager } from '@/components/risk/WalletManager';
import { UsageMonitor } from '@/components/risk/UsageMonitor';
import { ReportSelector, ReportForm, ReportResult } from '@/components/risk/ReportForm';
import { RiskHistory, RiskBlacklist, RiskAccessManager } from '@/components/risk/RiskPanels';
import AdminManagement from '@/components/admin/AdminManagement';
import ESignDocuments from '@/components/admin/ESignDocuments';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  LayoutDashboard,
  Users,
  Calendar,
  MessageSquare,
  FileText,
  LogOut,
  Menu,
  X,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Edit,
  Check,
  Clock,
  XCircle,
  CheckCircle,
  Mail,
  Phone,
  Building2,
  Loader2,
  RefreshCw,
  CreditCard,
  Euro,
  TrendingUp,
  ArrowUpRight,
  BarChart3,
  Search,
  Download,
  PenSquare,
  Save,
  Newspaper,
  Upload,
  BookOpen,
  Cpu,
  Facebook,
  BotMessageSquare,
  ToggleLeft,
  ToggleRight,
  HelpCircle,
  Lightbulb,
  ShieldAlert,
  History,
  AlertTriangle,
  Paperclip,
  Wallet,
  ExternalLink,
  FileSignature,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CSV Export utility
const exportToCSV = (data, columns, filename) => {
  const header = columns.map(c => c.label).join(',');
  const rows = data.map(row =>
    columns.map(c => {
      let val = typeof c.accessor === 'function' ? c.accessor(row) : (row[c.accessor] ?? '');
      val = String(val).replace(/"/g, '""');
      return `"${val}"`;
    }).join(',')
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

// Search filter utility
const matchesSearch = (item, query, fields) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return fields.some(f => {
    const val = typeof f === 'function' ? f(item) : item[f];
    return val && String(val).toLowerCase().includes(q);
  });
};

// Admin Auth Context
const useAdminAuth = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [adminInfo, setAdminInfo] = useState(() => {
    const saved = localStorage.getItem('admin_info');
    return saved ? JSON.parse(saved) : null;
  });
  
  const login = (newToken, info = null) => {
    localStorage.setItem('admin_token', newToken);
    if (info) {
      localStorage.setItem('admin_info', JSON.stringify(info));
      setAdminInfo(info);
    }
    setToken(newToken);
  };
  
  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_info');
    setToken(null);
    setAdminInfo(null);
  };
  
  return { token, login, logout, isAuthenticated: !!token, adminInfo };
};

// API helper with auth
const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============== ADMIN LOGIN ==============
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(response.data.access_token, { email: response.data.email, name: response.data.name, is_super_admin: response.data.is_super_admin });
      toast.success('Accesso effettuato');
    } catch (error) {
      toast.error('Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">SA</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          <p className="text-slate-400">Studio Professionale Azzano Cantarutti SRL STP</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-800 rounded-2xl p-8 space-y-6">
          <div>
            <Label className="text-slate-300">Username</Label>
            <Input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-2 bg-slate-700 border-slate-600 text-white"
              placeholder="admin"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              spellCheck="false"
              data-testid="admin-username-input"
            />
          </div>
          <div>
            <Label className="text-slate-300">Password</Label>
            <div className="relative mt-2">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white pr-10"
                placeholder="••••••••"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="current-password"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full btn-accent" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Accedi
          </Button>
        </form>

        <div className="text-center mt-6">
          <a href="https://telcavoip.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-teal-400 transition-colors">
            Made with <span className="text-red-400">&#9829;</span> by <span className="font-semibold text-teal-400">TelcaVoIP AI</span>
          </a>
        </div>
      </motion.div>
    </div>
  );
};

// ============== DASHBOARD ==============
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    fetchStats();
    adminApi.get('/admin/risk/wallet').then(r => setWallet(r.data)).catch(() => {});
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminApi.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento statistiche');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <Button variant="outline" onClick={fetchStats}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Users}
          label="Clienti Registrati"
          value={stats?.total_users || 0}
          color="indigo"
        />
        <StatCard
          icon={Calendar}
          label="Consulenze Totali"
          value={stats?.total_consultations || 0}
          color="green"
          badge={stats?.pending_consultations > 0 ? `${stats.pending_consultations} in attesa` : null}
        />
        <StatCard
          icon={MessageSquare}
          label="Messaggi"
          value={stats?.total_contacts || 0}
          color="amber"
          badge={stats?.unread_contacts > 0 ? `${stats.unread_contacts} non letti` : null}
        />
        <StatCard
          icon={Euro}
          label="Ricavi Totali"
          value={`€${stats?.total_revenue?.toFixed(2) || '0.00'}`}
          color="emerald"
          badge={stats?.paid_payments > 0 ? `${stats.paid_payments} pagati` : null}
        />
      </div>

      {/* Credito API CALLS AI RISK */}
      {wallet && (() => {
        const isSA = wallet.is_super_admin;
        const isBlocked = !isSA && wallet.balance <= 0;
        const isLow = !isSA && wallet.balance > 0 && wallet.balance <= (wallet.alert_threshold || 10);
        return (
        <div className={`rounded-xl border-2 p-5 ${
          isSA ? 'bg-teal-50 border-teal-200' :
          isBlocked ? 'bg-red-50 border-red-300' :
          isLow ? 'bg-amber-50 border-amber-300' :
          'bg-emerald-50 border-emerald-200'
        }`} data-testid="dashboard-risk-credit">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                isSA ? 'bg-teal-200 text-teal-700' :
                isBlocked ? 'bg-red-200 text-red-700' :
                isLow ? 'bg-amber-200 text-amber-700' :
                'bg-emerald-200 text-emerald-700'
              }`}>
                <Wallet className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Credito API CALLS AI RISK</p>
                <p className={`text-3xl font-bold ${
                  isSA ? 'text-teal-700' :
                  isBlocked ? 'text-red-700' :
                  isLow ? 'text-amber-700' :
                  'text-emerald-700'
                }`}>{wallet.balance.toFixed(2)}€</p>
              </div>
              {isSA && (
                <Badge className="bg-teal-200 text-teal-700 text-xs">SUPER ADMIN — SEMPRE ATTIVO</Badge>
              )}
              {isBlocked && (
                <Badge className="bg-red-200 text-red-700 text-xs">SERVIZIO BLOCCATO</Badge>
              )}
              {isLow && (
                <Badge className="bg-amber-200 text-amber-700 text-xs">CREDITO BASSO</Badge>
              )}
            </div>
            {!isSA && (
              <a href="https://telcavoip.ai/it/api-recharge" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-all hover:shadow-md"
                data-testid="dashboard-recharge-cta">
                <CreditCard className="w-4 h-4" />
                Ricarica Credito
                <ExternalLink className="w-3 h-3 ml-0.5 opacity-70" />
              </a>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, badge }) => {
  const colors = {
    indigo: 'bg-indigo-100 text-indigo-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
    emerald: 'bg-emerald-100 text-emerald-600',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        {badge && <Badge variant="secondary">{badge}</Badge>}
      </div>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </div>
  );
};

// ============== USERS MANAGEMENT ==============
const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminApi.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('Sei sicuro di voler eliminare questo utente?')) return;
    try {
      await adminApi.delete(`/admin/users/${userId}`);
      toast.success('Utente eliminato');
      setSelectedIds(prev => prev.filter(id => id !== userId));
      fetchUsers();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!confirm(`Sei sicuro di voler eliminare ${selectedIds.length} clienti? Questa azione è irreversibile.`)) return;
    try {
      const res = await adminApi.post('/admin/users/bulk-delete', { user_ids: selectedIds });
      toast.success(res.data.message);
      setSelectedIds([]);
      fetchUsers();
    } catch { toast.error('Errore nell\'eliminazione'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(u => u.id));
    }
  };

  const filtered = users.filter(u => matchesSearch(u, search, ['nome', 'cognome', 'email', 'azienda', 'telefono']));

  const handleExport = () => {
    exportToCSV(filtered, [
      { label: 'Nome', accessor: 'nome' },
      { label: 'Cognome', accessor: 'cognome' },
      { label: 'Email', accessor: 'email' },
      { label: 'Azienda', accessor: 'azienda' },
      { label: 'Telefono', accessor: 'telefono' },
      { label: 'Data Registrazione', accessor: 'created_at' },
    ], 'clienti');
    toast.success('File CSV esportato');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-slate-900">Gestione Clienti</h1>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={bulkDelete} data-testid="bulk-delete-users-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Elimina {selectedIds.length} selezionati
            </Button>
          )}
          <Button variant="outline" onClick={handleExport} data-testid="export-users-btn">
            <Download className="w-4 h-4 mr-2" />
            Esporta CSV
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="btn-accent">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Cliente
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Cerca per nome, email, azienda..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-users-input"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selectedIds.length === filtered.length}
                  onChange={toggleSelectAll}
                  className="rounded border-slate-300 cursor-pointer"
                  data-testid="select-all-users"
                />
              </TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Azienda</TableHead>
              <TableHead>Telefono</TableHead>
              <TableHead>Data Registrazione</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((user) => (
              <TableRow key={user.id} className={selectedIds.includes(user.id) ? 'bg-red-50' : ''}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(user.id)}
                    onChange={() => toggleSelect(user.id)}
                    className="rounded border-slate-300 cursor-pointer"
                  />
                </TableCell>
                <TableCell className="font-medium">{user.nome} {user.cognome}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.azienda || '-'}</TableCell>
                <TableCell>{user.telefono || '-'}</TableCell>
                <TableCell>{new Date(user.created_at).toLocaleDateString('it-IT')}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedUser(user)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteUser(user.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                  {search ? 'Nessun risultato trovato' : 'Nessun cliente registrato'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create User Dialog */}
      <CreateUserDialog 
        open={showCreateDialog} 
        onClose={() => setShowCreateDialog(false)}
        onSuccess={fetchUsers}
      />

      {/* User Detail Dialog */}
      {selectedUser && (
        <UserDetailDialog 
          user={selectedUser} 
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
};

const CreateUserDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nome: '', cognome: '', email: '', password: '', azienda: '', telefono: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.post('/admin/users', formData);
      toast.success('Cliente creato con successo');
      onClose();
      onSuccess();
      setFormData({ nome: '', cognome: '', email: '', password: '', azienda: '', telefono: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nuovo Cliente</DialogTitle>
          <DialogDescription>Inserisci i dati del nuovo cliente</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Cognome *</Label>
              <Input
                value={formData.cognome}
                onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                required
              />
            </div>
          </div>
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
          </div>
          <div>
            <Label>Password *</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required
            />
          </div>
          <div>
            <Label>Azienda</Label>
            <Input
              value={formData.azienda}
              onChange={(e) => setFormData({...formData, azienda: e.target.value})}
            />
          </div>
          <div>
            <Label>Telefono</Label>
            <Input
              value={formData.telefono}
              onChange={(e) => setFormData({...formData, telefono: e.target.value})}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="btn-accent" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Crea Cliente
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const UserDetailDialog = ({ user, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDetails();
  }, [user.id]);

  const fetchDetails = async () => {
    try {
      const response = await adminApi.get(`/admin/users/${user.id}`);
      setDetails(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{user.nome} {user.cognome}</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                <span>{user.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                <span>{user.telefono || '-'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-400" />
                <span>{user.azienda || '-'}</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Consulenze ({details?.consultations?.length || 0})</h4>
              {details?.consultations?.length > 0 ? (
                <div className="space-y-2">
                  {details.consultations.map((c) => (
                    <div key={c.id} className="bg-slate-50 p-3 rounded-lg flex justify-between">
                      <span>{c.tipo_consulenza}</span>
                      <span className="text-slate-500">{c.data_preferita}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">Nessuna consulenza</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============== CONSULTATIONS MANAGEMENT ==============
const ConsultationsManagement = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      const response = await adminApi.get('/admin/consultations');
      setConsultations(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, stato) => {
    try {
      await adminApi.put(`/admin/consultations/${id}`, { stato });
      toast.success('Stato aggiornato');
      fetchConsultations();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const deleteConsultation = async (id) => {
    if (!confirm('Eliminare questa consulenza?')) return;
    try {
      await adminApi.delete(`/admin/consultations/${id}`);
      toast.success('Consulenza eliminata');
      fetchConsultations();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const statusLabels = {
    pending: 'In attesa', confirmed: 'Confermata', completed: 'Completata', cancelled: 'Annullata',
  };

  const getStatusBadge = (stato) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      confirmed: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return <Badge className={styles[stato]}>{statusLabels[stato]}</Badge>;
  };

  const filtered = consultations.filter(c => matchesSearch(c, search, ['nome', 'cognome', 'email', 'telefono', 'tipo_consulenza', 'stato']));

  const handleExport = () => {
    exportToCSV(filtered, [
      { label: 'Nome', accessor: (c) => `${c.nome} ${c.cognome}` },
      { label: 'Email', accessor: 'email' },
      { label: 'Telefono', accessor: 'telefono' },
      { label: 'Tipo', accessor: 'tipo_consulenza' },
      { label: 'Data', accessor: 'data_preferita' },
      { label: 'Ora', accessor: 'ora_preferita' },
      { label: 'Stato', accessor: (c) => statusLabels[c.stato] || c.stato },
      { label: 'Creata il', accessor: 'created_at' },
    ], 'consulenze');
    toast.success('File CSV esportato');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-slate-900">Gestione Consulenze</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-consultations-btn">
            <Download className="w-4 h-4 mr-2" />
            Esporta CSV
          </Button>
          <Button variant="outline" onClick={fetchConsultations}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Cerca per nome, email, tipo, stato..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-consultations-input"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Data/Ora</TableHead>
              <TableHead>Contatti</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.nome} {c.cognome}</TableCell>
                <TableCell>{c.tipo_consulenza}</TableCell>
                <TableCell>{c.data_preferita} - {c.ora_preferita}</TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div>{c.email}</div>
                    <div className="text-slate-500">{c.telefono}</div>
                  </div>
                </TableCell>
                <TableCell>{getStatusBadge(c.stato)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Select onValueChange={(v) => updateStatus(c.id, v)}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Cambia stato" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">In attesa</SelectItem>
                        <SelectItem value="confirmed">Confermata</SelectItem>
                        <SelectItem value="completed">Completata</SelectItem>
                        <SelectItem value="cancelled">Annullata</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => deleteConsultation(c.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  {search ? 'Nessun risultato trovato' : 'Nessuna consulenza'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ============== CONTACTS MANAGEMENT ==============
const ContactsManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await adminApi.get('/admin/contacts');
      setContacts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id, letto) => {
    try {
      await adminApi.put(`/admin/contacts/${id}`, { letto });
      fetchContacts();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const deleteContact = async (id) => {
    if (!confirm('Eliminare questo messaggio?')) return;
    try {
      await adminApi.delete(`/admin/contacts/${id}`);
      toast.success('Messaggio eliminato');
      fetchContacts();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const filtered = contacts.filter(c => matchesSearch(c, search, ['nome', 'email', 'telefono', 'messaggio']));

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-slate-900">Messaggi Contatti</h1>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Cerca per nome, email, messaggio..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-contacts-input"
        />
      </div>

      <div className="space-y-4">
        {filtered.map((contact) => (
          <div 
            key={contact.id} 
            className={`bg-white rounded-xl border p-6 ${contact.letto ? 'border-slate-200' : 'border-indigo-300 bg-indigo-50/30'}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-slate-900">{contact.nome}</h3>
                <p className="text-sm text-slate-500">{contact.email} {contact.telefono && `• ${contact.telefono}`}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">
                  {new Date(contact.created_at).toLocaleString('it-IT')}
                </span>
                {!contact.letto && <Badge>Nuovo</Badge>}
              </div>
            </div>
            <p className="text-slate-700 mb-4">{contact.messaggio}</p>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => markAsRead(contact.id, !contact.letto)}
              >
                {contact.letto ? <XCircle className="w-4 h-4 mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                {contact.letto ? 'Segna non letto' : 'Segna come letto'}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.location.href = `mailto:${contact.email}`}
              >
                <Mail className="w-4 h-4 mr-2" />
                Rispondi
              </Button>
              <Button variant="ghost" size="sm" onClick={() => deleteContact(contact.id)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            {search ? 'Nessun risultato trovato' : 'Nessun messaggio ricevuto'}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== PAYMENTS MANAGEMENT ==============
const PaymentsManagement = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, statsRes] = await Promise.all([
        adminApi.get('/admin/payments'),
        adminApi.get('/admin/stats')
      ]);
      setPayments(paymentsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento pagamenti');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      paid: { cls: 'bg-green-100 text-green-700', label: 'Pagato' },
      pending: { cls: 'bg-amber-100 text-amber-700', label: 'In attesa' },
      unpaid: { cls: 'bg-slate-100 text-slate-600', label: 'Non pagato' },
      expired: { cls: 'bg-red-100 text-red-700', label: 'Scaduto' },
    };
    const s = map[status] || { cls: 'bg-slate-100 text-slate-600', label: status };
    return <Badge className={s.cls}>{s.label}</Badge>;
  };

  const consultationLabels = {
    'startup': 'Startup',
    'ecommerce': 'E-commerce',
    'business-model-test': 'Business Model Test',
    'inizio-attivita': 'Inizio attività',
    'fiscale-generale': 'Fiscale generale',
    'internazionale': 'Internazionale',
    'contabile': 'Contabile',
  };

  const paymentStatusLabels = { paid: 'Pagato', pending: 'In attesa', unpaid: 'Non pagato', expired: 'Scaduto' };

  const filtered = payments.filter(p => matchesSearch(p, search, [
    'email',
    (p) => `${p.metadata?.nome || ''} ${p.metadata?.cognome || ''}`,
    (p) => p.metadata?.tipo_consulenza || '',
    'payment_status',
  ]));

  const handleExport = () => {
    exportToCSV(filtered, [
      { label: 'Data', accessor: 'created_at' },
      { label: 'Cliente', accessor: (p) => `${p.metadata?.nome || ''} ${p.metadata?.cognome || ''}` },
      { label: 'Email', accessor: 'email' },
      { label: 'Consulenza', accessor: (p) => consultationLabels[p.metadata?.tipo_consulenza] || p.metadata?.tipo_consulenza || '' },
      { label: 'Importo (EUR)', accessor: (p) => p.amount?.toFixed(2) || '0.00' },
      { label: 'Stato', accessor: (p) => paymentStatusLabels[p.payment_status] || p.payment_status },
      { label: 'Session ID', accessor: 'session_id' },
    ], 'pagamenti');
    toast.success('File CSV esportato');
  };

  const clearAllPayments = async () => {
    if (!confirm('Sei sicuro di voler eliminare TUTTI i pagamenti? Questa azione è irreversibile.')) return;
    try {
      const res = await adminApi.delete('/admin/payments/clear-all');
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error('Errore durante l\'eliminazione'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6" data-testid="admin-payments-section">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-slate-900">Pagamenti</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="bg-red-600 text-white border-red-600 hover:bg-red-700" onClick={clearAllPayments} data-testid="clear-payments-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Azzera tutto
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-payments-btn">
            <Download className="w-4 h-4 mr-2" />
            Esporta CSV
          </Button>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-payments-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl p-5 text-white" data-testid="revenue-total-card">
          <div className="flex items-center justify-between mb-3">
            <Euro className="w-6 h-6 opacity-80" />
            <TrendingUp className="w-5 h-5 opacity-60" />
          </div>
          <p className="text-2xl font-bold">€{stats?.total_revenue?.toFixed(2) || '0.00'}</p>
          <p className="text-sm text-emerald-100 mt-1">Ricavi Totali</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="payments-total-card">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats?.total_payments || 0}</p>
          <p className="text-sm text-slate-500 mt-1">Transazioni Totali</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="payments-paid-card">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">{stats?.paid_payments || 0}</p>
          <p className="text-sm text-slate-500 mt-1">Pagati</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="payments-pending-card">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-amber-500" />
          </div>
          <p className="text-2xl font-bold text-amber-600">{stats?.pending_payments || 0}</p>
          <p className="text-sm text-slate-500 mt-1">In Attesa</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Cerca per cliente, email, tipo consulenza..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-payments-input"
        />
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Consulenza</TableHead>
              <TableHead>Importo</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Session ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id || p.session_id} data-testid={`payment-row-${p.session_id}`}>
                <TableCell className="text-sm text-slate-500">
                  {new Date(p.created_at).toLocaleString('it-IT', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </TableCell>
                <TableCell>
                  <div>
                    <span className="font-medium">{p.metadata?.nome} {p.metadata?.cognome}</span>
                    <div className="text-xs text-slate-400">{p.email}</div>
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {consultationLabels[p.metadata?.tipo_consulenza] || p.metadata?.tipo_consulenza || '-'}
                </TableCell>
                <TableCell className="font-semibold">€{p.amount?.toFixed(2)}</TableCell>
                <TableCell>{getStatusBadge(p.payment_status)}</TableCell>
                <TableCell>
                  <span className="text-xs text-slate-400 font-mono truncate block max-w-[120px]" title={p.session_id}>
                    {p.session_id?.slice(0, 16)}...
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  {search ? 'Nessun risultato trovato' : 'Nessuna transazione registrata'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ============== ANALYTICS DASHBOARD ==============
const CHART_COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const TIPO_LABELS = {
  'startup': 'Startup',
  'ecommerce': 'E-commerce',
  'business-model-test': 'Business Model Test',
  'inizio-attivita': 'Inizio Attività',
  'fiscale-generale': 'Fiscale',
  'internazionale': 'Internazionale',
  'contabile': 'Contabile'
};

const STATUS_LABELS = {
  'pending': 'In attesa', 'confirmed': 'Confermata', 'completed': 'Completata',
  'cancelled': 'Annullata', 'paid': 'Pagato', 'unpaid': 'Non pagato'
};

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  useEffect(() => { fetchAnalytics(); }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/analytics?period=${period}`);
      setAnalytics(r.data);
    } catch { toast.error('Errore nel caricamento analytics'); }
    finally { setLoading(false); }
  };

  if (loading) return <LoadingSpinner />;
  if (!analytics) return <div className="text-center py-12 text-slate-500">Dati non disponibili</div>;

  const monthLabel = (m) => {
    const [y, mo] = m.split('-');
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return `${months[parseInt(mo) - 1]} ${y.slice(2)}`;
  };

  const consultationData = analytics.consultations_by_month.map(c => ({ name: monthLabel(c.month), consulenze: c.count }));
  const revenueData = analytics.revenue_by_month.map(r => ({ name: monthLabel(r.month), ricavi: r.total, pagamenti: r.count }));
  const usersData = analytics.users_by_month.map(u => ({ name: monthLabel(u.month), clienti: u.count }));
  const typeData = analytics.consultations_by_type.map(c => ({ name: TIPO_LABELS[c.tipo] || c.tipo, value: c.count }));
  const statusData = analytics.consultation_status.map(c => ({ name: STATUS_LABELS[c.status] || c.status, value: c.count }));
  const paymentData = analytics.payment_distribution.map(p => ({ name: STATUS_LABELS[p.status] || p.status, value: p.count, total: p.total }));

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-slate-900">Analytics</h1>
        <div className="flex items-center gap-2">
          {[
            { value: '7d', label: '7gg' },
            { value: '30d', label: '30gg' },
            { value: '3m', label: '3 mesi' },
            { value: '6m', label: '6 mesi' },
            { value: '1y', label: '1 anno' },
            { value: 'all', label: 'Tutto' }
          ].map(p => (
            <Button
              key={p.value}
              variant={period === p.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(p.value)}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={fetchAnalytics} data-testid="refresh-analytics-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Time Series Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consultations Over Time */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-500" />Consulenze nel tempo
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={consultationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="consulenze" fill="#4F46E5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Over Time */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Euro className="w-5 h-5 text-emerald-500" />Ricavi nel tempo (€)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => `€${v}`} />
              <Line type="monotone" dataKey="ricavi" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* New Users Over Time */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />Nuovi clienti nel tempo
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={usersData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="clienti" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Consultations by Type - Pie */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-500" />Per tipo consulenza
          </h3>
          {typeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={typeData} cx="50%" cy="50%" outerRadius={90} innerRadius={40} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {typeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-center py-16">Nessun dato</p>}
        </div>

        {/* Consultation Status */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />Stato consulenze
          </h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" outerRadius={90} innerRadius={40} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {statusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-center py-16">Nessun dato</p>}
        </div>

        {/* Payment Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-500" />Distribuzione pagamenti
          </h3>
          {paymentData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={paymentData} cx="50%" cy="50%" outerRadius={90} innerRadius={40} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {paymentData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, name, props) => [v, `€${props.payload.total}`]} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-center py-16">Nessun dato</p>}
        </div>
      </div>
    </div>
  );
};

// ============== BLOG MANAGEMENT ==============
const BlogManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ titolo: '', sommario: '', contenuto: '', categoria: 'Fiscale', pubblicato: true, allegato_nome: null, allegato_file: null });

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await adminApi.get('/admin/blog');
      setPosts(res.data);
    } catch { toast.error('Errore caricamento articoli'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ titolo: '', sommario: '', contenuto: '', categoria: 'Fiscale', pubblicato: true, allegato_nome: null, allegato_file: null });
    setEditing(null);
  };

  const savePost = async () => {
    if (!form.titolo || !form.contenuto || !form.sommario) {
      toast.error('Compila titolo, sommario e contenuto');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await adminApi.put(`/admin/blog/${editing}`, form);
        toast.success('Articolo aggiornato');
      } else {
        await adminApi.post('/admin/blog', form);
        toast.success('Articolo creato');
      }
      resetForm();
      fetchPosts();
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const editPost = (post) => {
    setEditing(post.id);
    setForm({ titolo: post.titolo, sommario: post.sommario, contenuto: post.contenuto, categoria: post.categoria, pubblicato: post.pubblicato, allegato_nome: post.allegato_nome || null, allegato_file: post.allegato_file || null });
  };

  const deletePost = async (id) => {
    if (!confirm('Eliminare questo articolo?')) return;
    try {
      await adminApi.delete(`/admin/blog/${id}`);
      toast.success('Articolo eliminato');
      fetchPosts();
    } catch { toast.error('Errore eliminazione'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await adminApi.post('/admin/blog/upload-attachment', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, allegato_nome: res.data.allegato_nome, allegato_file: res.data.allegato_file }));
      toast.success(`File "${file.name}" caricato`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = async () => {
    if (form.allegato_file) {
      try { await adminApi.delete(`/admin/blog/attachment/${form.allegato_file}`); } catch {}
    }
    setForm(f => ({ ...f, allegato_nome: null, allegato_file: null }));
    toast.success('Allegato rimosso');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <h1 className="text-3xl font-bold text-slate-900">Gestione Novità</h1>

      {/* Editor */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <h2 className="font-semibold text-slate-700">{editing ? 'Modifica Articolo' : 'Nuovo Articolo'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input placeholder="Titolo *" value={form.titolo} onChange={e => setForm(f => ({...f, titolo: e.target.value}))} data-testid="blog-title-input" />
          <div className="flex gap-2">
            <select value={form.categoria} onChange={e => setForm(f => ({...f, categoria: e.target.value}))} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="Fiscale">Fiscale</option>
              <option value="Normativa">Normativa</option>
              <option value="Previdenziale">Previdenziale</option>
              <option value="Agevolazioni">Agevolazioni</option>
              <option value="Innovazione">Innovazione</option>
            </select>
            <label className="flex items-center gap-2 whitespace-nowrap text-sm">
              <input type="checkbox" checked={form.pubblicato} onChange={e => setForm(f => ({...f, pubblicato: e.target.checked}))} />
              Pubblicato
            </label>
          </div>
        </div>
        <Input placeholder="Sommario (anteprima) *" value={form.sommario} onChange={e => setForm(f => ({...f, sommario: e.target.value}))} data-testid="blog-summary-input" />
        <textarea
          placeholder="Contenuto completo dell'articolo *"
          value={form.contenuto}
          onChange={e => setForm(f => ({...f, contenuto: e.target.value}))}
          className="w-full min-h-[200px] px-3 py-2 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
          data-testid="blog-content-input"
        />

        {/* Allegato */}
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
          {form.allegato_nome ? (
            <>
              <FileText className="w-5 h-5 text-indigo-600 flex-shrink-0" />
              <span className="text-sm text-slate-700 flex-1 truncate">{form.allegato_nome}</span>
              <Button variant="ghost" size="sm" onClick={removeAttachment} data-testid="blog-remove-attachment">
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-500 flex-1">Nessun allegato</span>
              <Button variant="outline" size="sm" disabled={uploading} data-testid="blog-upload-attachment" onClick={() => document.getElementById('blog-file-upload').click()}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Carica file
              </Button>
              <input id="blog-file-upload" type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.jpg,.jpeg,.png" />
            </>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={savePost} className="btn-accent" disabled={saving} data-testid="blog-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {editing ? 'Aggiorna' : 'Pubblica'}
          </Button>
          {editing && <Button variant="outline" onClick={resetForm}>Annulla</Button>}
        </div>
      </div>

      {/* Posts List */}
      <div className="space-y-3">
        {posts.map(post => (
          <div key={post.id} className="bg-white rounded-xl border border-slate-200 p-5 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-slate-900 truncate">{post.titolo}</h3>
                <Badge variant={post.pubblicato ? 'default' : 'secondary'}>
                  {post.pubblicato ? 'Pubblicato' : 'Bozza'}
                </Badge>
                <Badge variant="outline">{post.categoria}</Badge>
              </div>
              <p className="text-sm text-slate-500 line-clamp-1">{post.sommario}</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-slate-400">{new Date(post.created_at).toLocaleDateString('it-IT')}</p>
                {post.allegato_nome && (
                  <span className="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                    <FileText className="w-3 h-3" />
                    {post.allegato_nome}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              {post.pubblicato && (
                <Button
                  variant="ghost"
                  size="sm"
                  title="Condividi su Facebook"
                  data-testid={`share-fb-${post.id}`}
                  onClick={() => {
                    const url = `${window.location.origin}/novita/${post.id}`;
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
                  }}
                >
                  <Facebook className="w-4 h-4 text-blue-600" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => editPost(post)}><Edit className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => deletePost(post.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <div className="text-center py-12 text-slate-500">Nessun articolo. Crea il primo!</div>}
      </div>
    </div>
  );
};

// ============== RAG DOCUMENTS MANAGEMENT ==============
const RagDocumentsManagement = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try {
      const res = await adminApi.get('/admin/rag-documents');
      setDocs(res.data);
    } catch { toast.error('Errore caricamento documenti'); }
    finally { setLoading(false); }
  };

  const processFiles = async (files) => {
    const pdfFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length === 0) {
      toast.error('Seleziona almeno un file PDF');
      return;
    }
    const nonPdf = files.length - pdfFiles.length;
    if (nonPdf > 0) toast.warning(`${nonPdf} file non-PDF ignorati`);

    setUploading(true);
    const queue = pdfFiles.map(f => ({ file: f, name: f.name, status: 'pending', chunks: 0, error: null }));
    setUploadQueue(queue);

    for (let i = 0; i < queue.length; i++) {
      setUploadQueue(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'uploading' } : item));
      try {
        const formData = new FormData();
        formData.append('file', queue[i].file);
        const res = await adminApi.post('/admin/rag-upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        setUploadQueue(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'done', chunks: res.data.chunks } : item));
      } catch (err) {
        const errMsg = err.response?.data?.detail || 'Errore upload';
        setUploadQueue(prev => prev.map((item, idx) => idx === i ? { ...item, status: 'error', error: errMsg } : item));
      }
    }
    setUploading(false);
    fetchDocs();
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.length) processFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };

  const deleteDoc = async (source) => {
    if (!confirm(`Rimuovere "${source}" dal chatbot?`)) return;
    try {
      await adminApi.delete(`/admin/rag-documents/${encodeURIComponent(source)}`);
      toast.success('Documento rimosso');
      fetchDocs();
    } catch { toast.error('Errore eliminazione'); }
  };

  const totalChunks = docs.reduce((sum, d) => sum + d.chunks, 0);
  const doneCount = uploadQueue.filter(q => q.status === 'done').length;
  const errorCount = uploadQueue.filter(q => q.status === 'error').length;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6" data-testid="admin-rag">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Documenti Chatbot</h1>
          <p className="text-slate-500 mt-1">Gestisci i documenti PDF che alimentano Tommy</p>
        </div>
        <Button className="btn-accent" disabled={uploading} data-testid="rag-upload-btn" onClick={() => fileInputRef.current?.click()}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
          Carica PDF
        </Button>
        <input ref={fileInputRef} type="file" accept=".pdf" multiple className="hidden" onChange={handleFileSelect} disabled={uploading} />
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          dragOver
            ? 'border-indigo-500 bg-indigo-50 scale-[1.01]'
            : 'border-slate-300 bg-slate-50 hover:border-indigo-400 hover:bg-indigo-50/50'
        }`}
        data-testid="rag-dropzone"
      >
        <div className="flex flex-col items-center gap-3">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
            dragOver ? 'bg-indigo-200' : 'bg-slate-200'
          }`}>
            <Upload className={`w-7 h-7 ${dragOver ? 'text-indigo-600' : 'text-slate-500'}`} />
          </div>
          <div>
            <p className="text-base font-semibold text-slate-700">
              {dragOver ? 'Rilascia i PDF qui' : 'Trascina i PDF qui oppure clicca per selezionarli'}
            </p>
            <p className="text-sm text-slate-400 mt-1">Puoi caricare più file PDF contemporaneamente</p>
          </div>
        </div>
      </div>

      {/* Upload Queue / Progress */}
      {uploadQueue.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="rag-upload-queue">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">
              {uploading ? 'Upload in corso...' : 'Upload completato'}
            </h3>
            <div className="flex items-center gap-2 text-xs">
              {doneCount > 0 && <span className="text-emerald-600 font-medium">{doneCount} completati</span>}
              {errorCount > 0 && <span className="text-red-600 font-medium">{errorCount} errori</span>}
              {uploading && <span className="text-indigo-600">{doneCount + errorCount}/{uploadQueue.length}</span>}
              {!uploading && <button onClick={() => setUploadQueue([])} className="text-slate-400 hover:text-slate-600 ml-2"><XCircle className="w-4 h-4" /></button>}
            </div>
          </div>
          {/* Global progress bar */}
          {uploading && (
            <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${((doneCount + errorCount) / uploadQueue.length) * 100}%` }} />
            </div>
          )}
          <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
            {uploadQueue.map((item, i) => (
              <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg text-sm ${
                item.status === 'done' ? 'bg-emerald-50' :
                item.status === 'error' ? 'bg-red-50' :
                item.status === 'uploading' ? 'bg-indigo-50' :
                'bg-slate-50'
              }`}>
                {item.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin text-indigo-500 flex-shrink-0" />}
                {item.status === 'done' && <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                {item.status === 'error' && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                {item.status === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" />}
                <span className="text-slate-700 truncate flex-1">{item.name}</span>
                {item.status === 'done' && <span className="text-xs text-emerald-600 font-medium flex-shrink-0">{item.chunks} sezioni</span>}
                {item.status === 'error' && <span className="text-xs text-red-500 truncate max-w-[200px] flex-shrink-0">{item.error}</span>}
                {item.status === 'uploading' && <span className="text-xs text-indigo-500 flex-shrink-0">Indicizzazione...</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center gap-4">
        <BookOpen className="w-6 h-6 text-indigo-600" />
        <div>
          <p className="font-semibold text-indigo-900">{docs.length} documenti - {totalChunks} sezioni indicizzate</p>
          <p className="text-sm text-indigo-600">Carica nuovi PDF per ampliare le conoscenze di Tommy</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Documento</TableHead>
              <TableHead>Sezioni</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map(doc => (
              <TableRow key={doc.source}>
                <TableCell className="font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  {doc.source}
                </TableCell>
                <TableCell>{doc.chunks}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => deleteDoc(doc.source)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {docs.length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-500">Nessun documento indicizzato</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ============== CMS CONTENT MANAGEMENT ==============
const CmsManagement = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchCms();
  }, []);

  const fetchCms = async () => {
    try {
      const response = await adminApi.get('/admin/cms');
      setPages(response.data);
      const initial = {};
      response.data.forEach(p => { initial[p.page_key] = { ...p.sections }; });
      setEditData(initial);
    } catch (error) {
      toast.error('Errore nel caricamento contenuti');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (pageKey, sectionKey, value) => {
    setEditData(prev => ({
      ...prev,
      [pageKey]: { ...prev[pageKey], [sectionKey]: value }
    }));
  };

  const savePage = async (pageKey) => {
    setSaving(prev => ({ ...prev, [pageKey]: true }));
    try {
      await adminApi.put(`/admin/cms/${pageKey}`, { sections: editData[pageKey] });
      toast.success('Contenuti salvati con successo');
      fetchCms();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(prev => ({ ...prev, [pageKey]: false }));
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-8" data-testid="admin-cms">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestione Contenuti</h1>
          <p className="text-slate-500 mt-1">Modifica i testi delle pagine del sito</p>
        </div>
      </div>

      {pages.map((page) => (
        <div key={page.page_key} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`cms-page-${page.page_key}`}>
          <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PenSquare className="w-5 h-5 text-indigo-600" />
              <div>
                <h2 className="font-semibold text-slate-900">{page.page_label}</h2>
                {page.updated_at && (
                  <p className="text-xs text-slate-400">Ultimo aggiornamento: {new Date(page.updated_at).toLocaleString('it-IT')}</p>
                )}
              </div>
            </div>
            <Button
              onClick={() => savePage(page.page_key)}
              className="btn-accent"
              disabled={saving[page.page_key]}
              data-testid={`save-cms-${page.page_key}`}
            >
              {saving[page.page_key] ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salva
            </Button>
          </div>
          <div className="p-6 space-y-5">
            {Object.keys(page.section_labels).map((sectionKey) => (
              <div key={sectionKey}>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  {page.section_labels[sectionKey]}
                </Label>
                {((editData[page.page_key]?.[sectionKey] || '').length > 80 || (editData[page.page_key]?.[sectionKey] || '').includes('\n')) ? (
                  <textarea
                    value={editData[page.page_key]?.[sectionKey] || ''}
                    onChange={(e) => handleChange(page.page_key, sectionKey, e.target.value)}
                    className="w-full min-h-[100px] px-3 py-2 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                    data-testid={`cms-field-${page.page_key}-${sectionKey}`}
                  />
                ) : (
                  <Input
                    value={editData[page.page_key]?.[sectionKey] || ''}
                    onChange={(e) => handleChange(page.page_key, sectionKey, e.target.value)}
                    data-testid={`cms-field-${page.page_key}-${sectionKey}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============== TELCAVOIP AI USAGE ==============
const AiUsageManagement = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => { fetchUsage(); }, [period]);

  const fetchUsage = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/ai-usage?period=${period}`);
      setData(r.data);
    } catch { toast.error('Errore caricamento dati AI'); }
    finally { setLoading(false); }
  };

  if (loading) return <LoadingSpinner />;
  if (!data) return <div className="text-center py-12 text-slate-500">Dati non disponibili</div>;

  const { totals, daily, recent_calls } = data;

  const chartData = daily.map(d => ({
    name: new Date(d.date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }),
    chiamate: d.calls,
    token: Math.round(d.tokens / 1000),
    costo: d.cost
  }));

  return (
    <div className="space-y-6" data-testid="admin-ai-usage">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">TelcaVoIP AI</h1>
          <p className="text-slate-500 mt-1">Monitoraggio utilizzo e costi intelligenza artificiale</p>
        </div>
        <div className="flex items-center gap-2">
          {[
            { value: '7d', label: '7gg' },
            { value: '30d', label: '30gg' },
            { value: '3m', label: '3 mesi' },
            { value: '6m', label: '6 mesi' },
            { value: '1y', label: '1 anno' },
            { value: 'all', label: 'Tutto' }
          ].map(p => (
            <Button
              key={p.value}
              variant={period === p.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(p.value)}
              data-testid={`ai-period-${p.value}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-xl p-5 text-white" data-testid="ai-total-calls">
          <div className="flex items-center justify-between mb-3">
            <Cpu className="w-6 h-6 opacity-80" />
            <TrendingUp className="w-5 h-5 opacity-60" />
          </div>
          <p className="text-2xl font-bold">{totals.total_calls.toLocaleString()}</p>
          <p className="text-sm text-cyan-100 mt-1">Chiamate AI totali</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ai-total-tokens">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-indigo-500" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{(totals.total_tokens / 1000).toFixed(1)}K</p>
          <p className="text-sm text-slate-500 mt-1">Token totali</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ai-rag-calls">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-violet-500" />
          </div>
          <p className="text-2xl font-bold text-violet-600">{totals.calls_with_rag}</p>
          <p className="text-sm text-slate-500 mt-1">Con documenti RAG</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white" data-testid="ai-total-cost">
          <div className="flex items-center justify-between mb-3">
            <Euro className="w-6 h-6 opacity-80" />
          </div>
          <p className="text-2xl font-bold">${totals.total_cost_usd.toFixed(4)}</p>
          <p className="text-sm text-amber-100 mt-1">Costo stimato (USD)</p>
        </div>
      </div>

      {/* Detail Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500 mb-1">Token input</p>
          <p className="text-xl font-bold text-slate-900">{(totals.total_input_tokens / 1000).toFixed(1)}K</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500 mb-1">Token output</p>
          <p className="text-xl font-bold text-slate-900">{(totals.total_output_tokens / 1000).toFixed(1)}K</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500 mb-1">Media token/chiamata</p>
          <p className="text-xl font-bold text-slate-900">{totals.avg_tokens_per_call.toLocaleString()}</p>
        </div>
      </div>

      {/* Charts */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-cyan-500" />Chiamate AI per giorno
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="chiamate" fill="#06B6D4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-500" />Token utilizzati (K) per giorno
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `${v}K token`} />
                <Line type="monotone" dataKey="token" stroke="#4F46E5" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Recent Calls */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Ultime chiamate AI</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data/Ora</TableHead>
              <TableHead>Modello</TableHead>
              <TableHead>Input</TableHead>
              <TableHead>Output</TableHead>
              <TableHead>Totale</TableHead>
              <TableHead>RAG</TableHead>
              <TableHead>Costo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recent_calls.map((call, i) => (
              <TableRow key={i}>
                <TableCell className="text-sm text-slate-500">
                  {new Date(call.timestamp).toLocaleString('it-IT', {
                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                  })}
                </TableCell>
                <TableCell><Badge variant="outline">{call.model}</Badge></TableCell>
                <TableCell className="text-sm">{call.input_tokens.toLocaleString()}</TableCell>
                <TableCell className="text-sm">{call.output_tokens.toLocaleString()}</TableCell>
                <TableCell className="text-sm font-medium">{call.total_tokens.toLocaleString()}</TableCell>
                <TableCell>
                  {call.context_used ? (
                    <Badge className="bg-violet-100 text-violet-700">Si</Badge>
                  ) : (
                    <Badge variant="secondary">No</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm font-medium">${call.cost_usd.toFixed(5)}</TableCell>
              </TableRow>
            ))}
            {recent_calls.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                  Nessuna chiamata AI registrata nel periodo selezionato
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ============== LOADING SPINNER ==============
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
  </div>
);

// ============== CHATBOT INSTRUCTIONS ==============
const ChatbotInstructions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newInstr, setNewInstr] = useState('');
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchItems = async () => {
    try {
      const res = await adminApi.get('/admin/chatbot-instructions');
      setItems(res.data);
    } catch { toast.error('Errore caricamento istruzioni'); }
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, []);

  const addInstruction = async () => {
    if (!newInstr.trim()) return;
    setSaving(true);
    try {
      await adminApi.post('/admin/chatbot-instructions', { type: 'instruction', content: newInstr });
      setNewInstr('');
      toast.success('Istruzione aggiunta');
      fetchItems();
    } catch { toast.error('Errore'); }
    setSaving(false);
  };

  const addFaq = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) return;
    setSaving(true);
    try {
      await adminApi.post('/admin/chatbot-instructions', { type: 'faq', ...newFaq });
      setNewFaq({ question: '', answer: '' });
      toast.success('FAQ aggiunta');
      fetchItems();
    } catch { toast.error('Errore'); }
    setSaving(false);
  };

  const toggleActive = async (item) => {
    try {
      await adminApi.put(`/admin/chatbot-instructions/${item.id}`, { active: !item.active });
      fetchItems();
    } catch { toast.error('Errore'); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm(item.type === 'faq' ? { question: item.question, answer: item.answer } : { content: item.content });
  };

  const saveEdit = async (item) => {
    try {
      await adminApi.put(`/admin/chatbot-instructions/${item.id}`, editForm);
      setEditingId(null);
      toast.success('Aggiornato');
      fetchItems();
    } catch { toast.error('Errore'); }
  };

  const deleteItem = async (id) => {
    if (!confirm('Eliminare questa voce?')) return;
    try {
      await adminApi.delete(`/admin/chatbot-instructions/${id}`);
      toast.success('Eliminato');
      fetchItems();
    } catch { toast.error('Errore'); }
  };

  const instructions = items.filter(i => i.type === 'instruction');
  const faqs = items.filter(i => i.type === 'faq');

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Istruzioni Chatbot</h1>
        <Badge variant="outline" className="text-sm">{items.filter(i => i.active).length} attive su {items.length}</Badge>
      </div>

      {/* Instructions Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold">Istruzioni Generiche</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Istruzioni aggiuntive che guidano il comportamento di Tommy. Es: "Consiglia sempre una consulenza per questioni complesse", "Menziona sempre il servizio di Innovazione Digitale".
        </p>

        {/* Add new instruction */}
        <div className="flex gap-2 mb-4">
          <Input
            value={newInstr}
            onChange={e => setNewInstr(e.target.value)}
            placeholder="Scrivi una nuova istruzione..."
            className="flex-1"
            onKeyDown={e => e.key === 'Enter' && addInstruction()}
            data-testid="new-instruction-input"
          />
          <Button onClick={addInstruction} disabled={saving || !newInstr.trim()} data-testid="add-instruction-btn">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>

        {/* Instruction list */}
        <div className="space-y-2">
          {instructions.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Nessuna istruzione aggiunta</p>}
          {instructions.map(item => (
            <div key={item.id} className={`flex items-start gap-3 p-3 rounded-lg border ${item.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`}>
              <button onClick={() => toggleActive(item)} className="mt-0.5 flex-shrink-0" title={item.active ? 'Disattiva' : 'Attiva'}>
                {item.active ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
              </button>
              <div className="flex-1 min-w-0">
                {editingId === item.id ? (
                  <div className="flex gap-2">
                    <Input value={editForm.content} onChange={e => setEditForm({content: e.target.value})} className="flex-1" />
                    <Button size="sm" onClick={() => saveEdit(item)}><Check className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-700">{item.content}</p>
                )}
              </div>
              {editingId !== item.id && (
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(item)}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteItem(item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-indigo-500" />
          <h2 className="text-lg font-semibold">FAQ Personalizzate</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Coppie domanda/risposta che Tommy userà per rispondere in modo preciso. Es: "Quali servizi offrite?" → "Lo Studio offre consulenza fiscale, contabile..."
        </p>

        {/* Add new FAQ */}
        <div className="space-y-2 mb-4 p-4 bg-slate-50 rounded-lg border border-dashed border-slate-300">
          <Input
            value={newFaq.question}
            onChange={e => setNewFaq(f => ({...f, question: e.target.value}))}
            placeholder="Domanda (es: Quali sono i vostri orari?)"
            data-testid="new-faq-question-input"
          />
          <textarea
            value={newFaq.answer}
            onChange={e => setNewFaq(f => ({...f, answer: e.target.value}))}
            placeholder="Risposta (es: Lo studio è aperto dal lunedì al venerdì dalle 9:00 alle 18:00)"
            className="w-full min-h-[80px] px-3 py-2 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
            data-testid="new-faq-answer-input"
          />
          <Button onClick={addFaq} disabled={saving || !newFaq.question.trim() || !newFaq.answer.trim()} className="w-full" data-testid="add-faq-btn">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi FAQ
          </Button>
        </div>

        {/* FAQ list */}
        <div className="space-y-3">
          {faqs.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Nessuna FAQ aggiunta</p>}
          {faqs.map(item => (
            <div key={item.id} className={`p-4 rounded-lg border ${item.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`}>
              <div className="flex items-start gap-3">
                <button onClick={() => toggleActive(item)} className="mt-0.5 flex-shrink-0" title={item.active ? 'Disattiva' : 'Attiva'}>
                  {item.active ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                </button>
                <div className="flex-1 min-w-0">
                  {editingId === item.id ? (
                    <div className="space-y-2">
                      <Input value={editForm.question} onChange={e => setEditForm(f => ({...f, question: e.target.value}))} placeholder="Domanda" />
                      <textarea
                        value={editForm.answer}
                        onChange={e => setEditForm(f => ({...f, answer: e.target.value}))}
                        className="w-full min-h-[80px] px-3 py-2 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                        placeholder="Risposta"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEdit(item)}><Check className="w-4 h-4 mr-1" /> Salva</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Annulla</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium text-sm text-slate-900">D: {item.question}</p>
                      <p className="text-sm text-slate-600 mt-1">R: {item.answer}</p>
                    </>
                  )}
                </div>
                {editingId !== item.id && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(item)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteItem(item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== TELCAVOIP RISK INTELLIGENCE ==============

const RiskIntelligence = () => {
  const [selectedReport, setSelectedReport] = useState('verifica_cf');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [showBlacklist, setShowBlacklist] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [showWallet, setShowWallet] = useState(false);
  const [walletTxns, setWalletTxns] = useState([]);
  const [env, setEnv] = useState('test');
  const [switching, setSwitching] = useState(false);
  const [usersAccess, setUsersAccess] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [blForm, setBlForm] = useState({ codice_fiscale: '', nome: '', cognome: '', motivo: '' });

  useEffect(() => {
    adminApi.get('/admin/risk/report-types').then(r => setEnv(r.data.environment)).catch(() => {});
    adminApi.get('/admin/risk/wallet').then(r => setWallet(r.data)).catch(() => {});
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try { const res = await adminApi.get('/admin/risk/history'); setHistory(res.data); } catch {}
  };
  const fetchUsersAccess = async () => {
    try { const res = await adminApi.get('/admin/risk/users-access'); setUsersAccess(res.data); } catch {}
  };
  const [envError, setEnvError] = useState(null);
  const switchEnv = async () => {
    const newEnv = env === 'test' ? 'live' : 'test';
    if (newEnv === 'live' && !confirm('Sei sicuro di voler passare all\'ambiente LIVE? Le query consumeranno credito reale.')) return;
    setSwitching(true);
    setEnvError(null);
    try {
      const res = await adminApi.post('/admin/risk/switch-env', { env: newEnv });
      setEnv(res.data.environment);
      toast.success(`Ambiente cambiato a ${res.data.environment.toUpperCase()}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402 && detail?.type === 'insufficient_credit_env') {
        setEnvError(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Errore nel cambio ambiente');
      }
    }
    setSwitching(false);
  };
  const toggleUserAccess = async (userId) => {
    try { const res = await adminApi.post(`/admin/risk/toggle-access/${userId}`); toast.success(res.data.message); fetchUsersAccess(); } catch { toast.error('Errore'); }
  };
  const fetchBlacklist = async () => {
    try { const res = await adminApi.get('/admin/risk/blacklist'); setBlacklist(res.data); } catch {}
  };
  const addToBlacklist = async () => {
    if (!blForm.codice_fiscale && (!blForm.nome || !blForm.cognome)) { toast.error('Inserisci CF oppure Nome e Cognome'); return; }
    try {
      await adminApi.post('/admin/risk/blacklist', blForm);
      toast.success('Aggiunto alla lista esclusione');
      setBlForm({ codice_fiscale: '', nome: '', cognome: '', motivo: '' });
      fetchBlacklist();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const removeFromBlacklist = async (id) => {
    if (!confirm('Rimuovere dalla lista di esclusione?')) return;
    try { await adminApi.delete(`/admin/risk/blacklist/${id}`); toast.success('Rimosso'); fetchBlacklist(); } catch { toast.error('Errore'); }
  };
  const [creditError, setCreditError] = useState(null);

  const handleQueryError = (err) => {
    const detail = err.response?.data?.detail;
    if (err.response?.status === 402 && detail?.type === 'insufficient_credit') {
      setCreditError(detail);
    } else {
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Errore nella richiesta');
    }
  };
  const executeQuery = async (formData) => {
    setLoading(true);
    setResult(null);
    setCreditError(null);
    try {
      const res = await adminApi.post('/admin/risk/query', formData);
      setResult(res.data);
      fetchHistory();
      if (res.data.success) toast.success('Report completato');
      else toast.error(res.data.error || 'Errore nella richiesta');
    } catch (err) { handleQueryError(err); }
    setLoading(false);
  };
  const executeCrifQuery = async (crifData) => {
    setLoading(true);
    setResult(null);
    setCreditError(null);
    try {
      const res = await adminApi.post('/admin/risk/query', { report_type: selectedReport, crif_data: crifData });
      setResult(res.data);
      fetchHistory();
      if (res.data.success) toast.success('Richiesta CRIF inviata con successo');
      else toast.error(res.data.error || 'Errore nella richiesta CRIF');
    } catch (err) { handleQueryError(err); }
    setLoading(false);
  };
  const checkReportStatus = async (reportType, requestId) => {
    setCheckingStatus(true);
    try {
      const res = await adminApi.get(`/admin/risk/check-status/${reportType}/${requestId}`);
      const newState = res.data.state;
      setResult(prev => ({ ...prev, data: res.data.data, state: newState, success: res.data.success, has_download: res.data.has_download, error: res.data.error }));
      if (newState === 'COMPLETE') { toast.success('Report completato! I risultati sono disponibili.'); fetchHistory(); }
      else if (newState === 'PENDING') toast.info('Il report è ancora in elaborazione. Riprova tra qualche minuto.');
      else toast.info(`Stato: ${newState}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore nel controllo stato'); }
    setCheckingStatus(false);
  };
  const downloadReport = async (reportType, requestId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/risk/download/${reportType}/${requestId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Download non disponibile'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `report_${reportType}_${requestId}.pdf`; link.click();
      URL.revokeObjectURL(url);
      toast.success('Download avviato');
    } catch { toast.error('Errore durante il download'); }
  };
  const clearHistory = async () => {
    if (!confirm('Eliminare tutta la cronologia delle ricerche?')) return;
    try { await adminApi.delete('/admin/risk/history'); setHistory([]); toast.success('Cronologia eliminata'); } catch { toast.error('Errore'); }
  };
  const fetchUsageStats = async () => {
    setLoadingUsage(true);
    try {
      const res = await adminApi.get('/admin/risk/usage-stats');
      setUsageStats(res.data);
      if (res.data.wallet) setWallet(prev => ({ ...prev, ...res.data.wallet }));
    } catch { toast.error('Errore nel caricamento statistiche'); }
    setLoadingUsage(false);
  };
  const fetchWallet = async () => {
    try { const res = await adminApi.get('/admin/risk/wallet'); setWallet(res.data); } catch {}
  };
  const handleTopup = async (amount, note) => {
    try {
      const res = await adminApi.post('/admin/risk/wallet/topup', { amount, note });
      toast.success(res.data.message);
      fetchWallet(); fetchWalletTxns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const handleDeduct = async (amount, note) => {
    try {
      const res = await adminApi.post('/admin/risk/wallet/deduct', { amount, note });
      toast.success(res.data.message);
      fetchWallet(); fetchWalletTxns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const updateWalletSettings = async (field, value) => {
    try {
      await adminApi.put('/admin/risk/wallet/settings', { [field]: parseFloat(value) });
      toast.success('Impostazione aggiornata');
      fetchWallet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const fetchWalletTxns = async () => {
    try { const res = await adminApi.get('/admin/risk/wallet/transactions'); setWalletTxns(res.data.transactions || []); } catch {}
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">TelcaVoIP Risk Intelligence</h1>
            <p className="text-sm text-slate-500">Analisi rischio e visure su persone e aziende</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={switchEnv} disabled={switching}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
              env === 'test' ? 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200'
            }`} data-testid="risk-switch-env">
            {switching ? <Loader2 className="w-3 h-3 animate-spin" /> : (env === 'test' ? <ToggleLeft className="w-4 h-4" /> : <ToggleRight className="w-4 h-4" />)}
            {env === 'test' ? 'Ambiente TEST' : 'Ambiente LIVE'}
          </button>
          <Button variant="outline" size="sm" onClick={() => { setShowAccess(!showAccess); if (!showAccess) fetchUsersAccess(); }} data-testid="risk-toggle-access">
            <Users className="w-4 h-4 mr-1" /> Accesso Clienti
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowBlacklist(!showBlacklist); if (!showBlacklist) fetchBlacklist(); }} data-testid="risk-toggle-blacklist">
            <XCircle className="w-4 h-4 mr-1" /> Esclusioni
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} data-testid="risk-toggle-history">
            <History className="w-4 h-4 mr-1" /> Cronologia
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowUsage(!showUsage); if (!showUsage) fetchUsageStats(); }} data-testid="risk-toggle-usage">
            <BarChart3 className="w-4 h-4 mr-1" /> Utilizzo API
          </Button>
          {wallet?.is_super_admin && (
            <Button variant="outline" size="sm" onClick={() => { setShowWallet(!showWallet); if (!showWallet) { fetchWallet(); fetchWalletTxns(); } }} data-testid="risk-toggle-wallet">
              <Wallet className="w-4 h-4 mr-1" /> Gestione Credito
            </Button>
          )}
        </div>
      </div>

      <WalletWidget wallet={wallet} onShowPricing={() => { setShowUsage(true); fetchUsageStats(); setTimeout(() => document.getElementById('risk-pricing-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300); }} />
      {envError && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 flex items-center gap-4 flex-wrap" data-testid="risk-env-error">
          <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">{envError}</p>
          </div>
          <a href="https://telcavoip.ai/it/api-recharge" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-all hover:shadow-md"
            data-testid="env-error-recharge-btn">
            <CreditCard className="w-4 h-4" />
            Ricarica Credito
            <ExternalLink className="w-3 h-3 ml-0.5 opacity-70" />
          </a>
          <button onClick={() => setEnvError(null)} className="text-amber-400 hover:text-amber-600 p-1"><XCircle className="w-5 h-5" /></button>
        </div>
      )}
      {showWallet && wallet?.is_super_admin && (
        <WalletManager wallet={wallet} onTopup={handleTopup} onDeduct={handleDeduct} onUpdateSettings={updateWalletSettings} walletTxns={walletTxns} />
      )}
      {showAccess && <RiskAccessManager usersAccess={usersAccess} onToggle={toggleUserAccess} />}
      {showBlacklist && <RiskBlacklist blacklist={blacklist} blForm={blForm} setBlForm={setBlForm} onAdd={addToBlacklist} onRemove={removeFromBlacklist} />}
      <ReportSelector selectedReport={selectedReport} onSelect={(v) => { setSelectedReport(v); setResult(null); }} />
      <ReportForm selectedReport={selectedReport} onSubmit={executeQuery} onCrifSubmit={executeCrifQuery} loading={loading} />
      {creditError && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5" data-testid="risk-credit-error">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Wallet className="w-5 h-5 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-red-800 mb-1">Credito insufficiente</h3>
              <p className="text-sm text-red-700 mb-3">
                Il credito attuale (<strong>{creditError.balance?.toFixed(2)}€</strong>) non è sufficiente per procedere con la richiesta
                "<strong>{creditError.report_label}</strong>" (costo: <strong>{creditError.required?.toFixed(2)}€</strong>).
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => { setShowUsage(true); fetchUsageStats(); setTimeout(() => document.getElementById('risk-pricing-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300); }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-all"
                  data-testid="credit-error-pricing-btn">
                  <FileText className="w-4 h-4 text-teal-600" />
                  Verifica il costo
                </button>
                <a href="https://telcavoip.ai/it/api-recharge" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-all hover:shadow-md"
                  data-testid="credit-error-recharge-btn">
                  <CreditCard className="w-4 h-4" />
                  Ricarica credito API
                  <ExternalLink className="w-3 h-3 ml-0.5 opacity-70" />
                </a>
              </div>
            </div>
            <button onClick={() => setCreditError(null)} className="text-red-400 hover:text-red-600 p-1" data-testid="credit-error-close">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
      <ReportResult result={result} onCheckStatus={checkReportStatus} onDownload={downloadReport} checkingStatus={checkingStatus} />
      {showHistory && <RiskHistory history={history} onClear={clearHistory} />}
      {showUsage && <UsageMonitor usageStats={usageStats} loadingUsage={loadingUsage} onRefresh={fetchUsageStats} wallet={wallet} />}
    </div>
  );
};

// ============== TELCAVOIP AI ASSISTANT ==============
const AiAssistant = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).substr(2, 9));
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);

  const actionLabels = {
    update_cms: { icon: PenSquare, color: 'text-blue-600 bg-blue-50', label: 'CMS aggiornato' },
    add_faq: { icon: HelpCircle, color: 'text-emerald-600 bg-emerald-50', label: 'FAQ aggiunta' },
    add_instruction: { icon: Lightbulb, color: 'text-amber-600 bg-amber-50', label: 'Istruzione aggiunta' },
    add_blog: { icon: Newspaper, color: 'text-purple-600 bg-purple-50', label: 'Articolo creato' },
    info: { icon: BotMessageSquare, color: 'text-slate-600 bg-slate-50', label: 'Risposta' },
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    try {
      const res = await adminApi.post('/admin/ai-assistant', { message: userMsg, session_id: sessionId });
      const d = res.data;
      setMessages(prev => [...prev, {
        role: 'assistant', text: d.summary, action: d.action, executed: d.executed, details: d.details
      }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Errore nella comunicazione con l\'AI Engine.', action: 'info', executed: false }]);
      toast.error('Errore AI Engine');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
          <Cpu className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Assistente AI</h1>
          <p className="text-sm text-slate-500">Scrivi cosa vuoi modificare e l'AI Engine di TelcaVoIP lo fa per te</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 flex flex-col" style={{ height: 'calc(100vh - 220px)' }}>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Cpu className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">TelcaVoIP AI Engine</h3>
              <p className="text-slate-500 max-w-md mx-auto mb-6">Scrivi cosa vuoi modificare nel sito e l'AI Engine lo farà per te. Le modifiche sono immediate.</p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  'Aggiungi una FAQ sulle scadenze fiscali',
                  'Aggiungi una FAQ sugli orari e contatti dello studio',
                  'Istruisci Tommy a consigliare sempre una consulenza',
                  'Crea un articolo sul regime forfettario',
                  'Crea un articolo sulle novità fiscali 2026',
                  'Cambia il titolo della homepage',
                  'Aggiorna la descrizione della pagina Chi Sono',
                  'Modifica il sottotitolo della pagina Servizi',
                ].map((s, i) => (
                  <button key={i} onClick={() => { setInput(s); }} className="text-sm px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-700 transition-all shadow-sm hover:shadow-md">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-800'
              }`}>
                {msg.role === 'assistant' && msg.executed && (
                  <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full mb-2 ${actionLabels[msg.action]?.color || 'bg-slate-50 text-slate-600'}`}>
                    <CheckCircle className="w-3 h-3" />
                    {actionLabels[msg.action]?.label || 'Completato'}
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                <span className="text-sm text-slate-500">TelcaVoIP AI sta elaborando...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Scrivi cosa vuoi modificare..."
              className="flex-1"
              disabled={loading}
              data-testid="ai-assistant-input"
            />
            <Button onClick={sendMessage} disabled={loading || !input.trim()} className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700" data-testid="ai-assistant-send">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== MAIN ADMIN PAGE ==============
const AdminPage = () => {
  const { token, login, logout, isAuthenticated, adminInfo } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const mainContentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (mainContentRef.current) mainContentRef.current.scrollTop = 0;
  }, [location.pathname]);

  if (!isAuthenticated) {
    return <AdminLogin onLogin={login} />;
  }

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/clienti', label: 'Clienti', icon: Users },
    { path: '/admin/consulenze', label: 'Consulenze', icon: Calendar },
    { path: '/admin/pagamenti', label: 'Pagamenti', icon: CreditCard },
    { path: '/admin/messaggi', label: 'Messaggi', icon: MessageSquare },
    { path: '/admin/contenuti', label: 'Contenuti', icon: PenSquare },
    { path: '/admin/novita', label: 'Novità', icon: Newspaper },
    { path: '/admin/documenti-chatbot', label: 'Documenti Chatbot', icon: BookOpen },
    { path: '/admin/firma-elettronica', label: 'Firma Elettronica', icon: FileSignature },
    ...(adminInfo?.is_super_admin ? [{ path: '/admin/gestione-admin', label: 'Gestione Admin', icon: ShieldAlert }] : []),
  ];

  const aiItems = [
    { path: '/admin/ai-assistente', label: 'Assistente AI', icon: BotMessageSquare },
    { path: '/admin/risk-intelligence', label: 'Risk Intelligence', icon: ShieldAlert },
    { path: '/admin/istruzioni-chatbot', label: 'Istruzioni Chatbot', icon: Lightbulb },
    { path: '/admin/telcavoip-ai', label: 'Costi & Monitoraggio', icon: BarChart3 },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)}>
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-semibold text-sm">{adminInfo?.name || 'Admin Panel'}</span>
        <div className="w-6" />
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-64 h-screen bg-slate-900 text-white
          flex flex-col transform transition-transform lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 border-b border-slate-800 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">SA</span>
              </div>
              <div>
                <p className="font-semibold">Admin Panel</p>
                <p className="text-xs text-slate-400">Studio Azzano STP</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}

            {/* TelcaVoIP AI Engine Section */}
            <div className="pt-4 mt-4 border-t border-slate-700">
              <div className="flex items-center gap-2 px-4 py-2 mb-1">
                <Cpu className="w-4 h-4 text-teal-400" />
                <span className="text-xs font-semibold uppercase tracking-wider text-teal-400">TelcaVoIP AI Engine</span>
              </div>
              {aiItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-teal-600 text-white'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>

          <div className="p-4 border-t border-slate-800 bg-slate-950 flex-shrink-0 space-y-3">
            {adminInfo && (
              <div className="flex items-center gap-3 px-3">
                <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">{adminInfo.name?.charAt(0)?.toUpperCase() || 'A'}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">{adminInfo.name}</p>
                  <p className="text-xs text-slate-400 truncate">{adminInfo.email}</p>
                </div>
                {adminInfo.is_super_admin && (
                  <span className="text-[10px] font-bold text-teal-400 bg-teal-900/50 px-1.5 py-0.5 rounded flex-shrink-0">SA</span>
                )}
              </div>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg w-full bg-red-900/60 text-red-300 hover:bg-red-600 hover:text-white transition-colors font-medium"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Esci
            </button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 overflow-y-auto" ref={mainContentRef}>
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="clienti" element={<UsersManagement />} />
            <Route path="consulenze" element={<ConsultationsManagement />} />
            <Route path="pagamenti" element={<PaymentsManagement />} />
            <Route path="messaggi" element={<ContactsManagement />} />
            <Route path="contenuti" element={<CmsManagement />} />
            <Route path="novita" element={<BlogManagement />} />
            <Route path="documenti-chatbot" element={<RagDocumentsManagement />} />
            <Route path="firma-elettronica" element={<ESignDocuments isSuperAdmin={adminInfo?.is_super_admin} />} />
            <Route path="ai-assistente" element={<AiAssistant />} />
            <Route path="risk-intelligence" element={<RiskIntelligence />} />
            <Route path="istruzioni-chatbot" element={<ChatbotInstructions />} />
            <Route path="telcavoip-ai" element={<AiUsageManagement />} />
            {adminInfo?.is_super_admin && <Route path="gestione-admin" element={<AdminManagement />} />}
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
